import type { RiskGapModel, RiskGapUserStatus } from '@ctw/shared/api/fhir/models/risk-gap';
import { riskGapDefaultSubstatuses } from '@ctw/shared/api/fhir/models/risk-gap-v2';
import { Badge } from '@ctw/shared/components/badge';
import { Button } from '@ctw/shared/components/button';
import { RiskGapCopyButton } from '@ctw/shared/content/risk/risk-gap-copy-button';
import { riskGapStatusBadgeDefinitionV2 } from '@ctw/shared/content/risk/risk-gap-status-badge-definition-v2';
import { buildRiskGapTelemetryContext } from '@ctw/shared/content/risk/utils';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { useRiskGapSummaryNoteContext } from '@ctw/shared/context/controlled-text-context';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faClock, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { RiskGapStatus as RiskGapBackwardsCompatibleStatus } from '@zeus-health/types';
import { useMemo, useState } from 'react';
import { RiskGapStatusDialogContent } from './risk-gap-status-dialog';

export interface RiskGapStatusFormV2Props {
  riskGap: RiskGapModel;
  currentStatus: RiskGapBackwardsCompatibleStatus;
  /** Called with note, status, and substatuses together — status and note changes
   *  are always submitted as a single action. Users cannot set 'closed-gap' (system-only)
   *  but can reopen a closed gap by selecting any user-selectable status. */
  onSave?: (
    note: string,
    status: RiskGapUserStatus,
    substatuses?: Array<string>,
  ) => void | Promise<void>;
}

/**
 * Renders the status section of a risk gap detail view. For open gaps, shows an inline
 * note editor and action buttons (Pending, Dismiss, Accept). For non-open gaps (including
 * closed), shows a read-only note and an "Edit Status" pencil button that opens the
 * status dialog. Users can reopen closed gaps through this flow — the dialog defaults
 * to 'open-gap' for closed gaps since 'closed-gap' is system-managed and not selectable.
 */
export const RiskGapStatusFormV2 = ({
  riskGap,
  currentStatus,
  onSave,
}: RiskGapStatusFormV2Props) => {
  const { controlledText: note, setControlledText: setNote } = useRiskGapSummaryNoteContext();
  const [isConfirming, setIsConfirming] = useState(false);
  const statusBadge = riskGapStatusBadgeDefinitionV2(riskGap);
  const builderConfig = useBuilderConfig();

  const telemetry = useTelemetry();
  const { openModal, closeModal } = useModal({
    boundaryName: 'StatusDialogModal',
    layout: { fullbleed: false, mode: 'fit', overflowBehavior: 'scroll', showCloseButton: false },
  });

  const substatusOptions = useMemo(() => {
    return (
      builderConfig.riskGaps?.statusOptions?.[riskGap.status]?.substatusOptions ??
      riskGapDefaultSubstatuses[riskGap.status as RiskGapBackwardsCompatibleStatus] ??
      []
    );
  }, [builderConfig, riskGap.status]);

  const isPendingGap = (status: RiskGapBackwardsCompatibleStatus) => status === 'pending';
  const resolveSubstatusLabel = (code: string) =>
    substatusOptions.find((option) => option.value === code)?.label ?? code;
  const handleNoteChange = (note: string) => {
    setNote(note);
  };

  const handleCancel = () => {
    telemetry.trackInteraction(
      'risk_gap_status_dialog_close',
      buildRiskGapTelemetryContext(riskGap),
    );
    setIsConfirming(false);
    closeModal();
  };

  const handleConfirmWithSubstatuses = async (
    callback:
      | ((
          notes: string,
          status: RiskGapUserStatus,
          substatuses?: Array<string>,
        ) => void | Promise<void>)
      | undefined,
    status: RiskGapUserStatus,
    substatuses: Array<string>,
    pendingNote: string,
  ) => {
    if (!callback) {
      return;
    }
    telemetry.trackInteraction('risk_gap_status_dialog_save', {
      ...buildRiskGapTelemetryContext(riskGap),
      riskGapPreviousStatus: currentStatus,
      riskGapNewStatus: status,
      riskGapNewSubstatuses: substatuses,
      riskGapNoteChanged: pendingNote !== (note ?? ''),
    });
    try {
      await callback(pendingNote, status, substatuses);
      closeModal();
    } finally {
      setIsConfirming(false);
    }
  };

  const handleAction = (status: RiskGapUserStatus) => {
    telemetry.trackInteraction('risk_gap_status_dialog_open', {
      ...buildRiskGapTelemetryContext(riskGap),
      riskGapTargetStatus: status,
    });
    setIsConfirming(true);
    openModal({
      telemetry,
      action: {
        content: {
          body: (
            <RiskGapStatusDialogContent
              note={note ?? ''}
              status={status}
              onConfirm={(pendingStatus, substatuses, pendingNote) => {
                setNote(pendingNote);
                return handleConfirmWithSubstatuses(
                  onSave,
                  pendingStatus,
                  substatuses,
                  pendingNote,
                );
              }}
              onCancel={handleCancel}
            />
          ),
        },
      },
    });
  };

  return (
    <div className={tw`flex w-full flex-col gap-4 transition-all duration-100`}>
      {!riskGap.showPrimaryEditExperience && note && (
        <div className="flex flex-col">
          <div className={tw`mb-1 flex items-center text-content-subtle text-xs uppercase`}>
            Note
          </div>
          <div className={tw`mb-2 whitespace-pre-wrap text-sm`}>{note}</div>
        </div>
      )}
      {riskGap.showPrimaryEditExperience && (
        <div className="relative flex">
          <textarea
            id="risk-gap-status-note"
            placeholder="Add Note..."
            onChange={(event) => {
              if (!isConfirming) {
                handleNoteChange(event.currentTarget.value);
              }
            }}
            value={note ?? ''}
            rows={4}
            className={tw`min-h-[50px] w-full border border-meta-background shadow-none focus:outline-double focus:outline-meta-background`}
          />
          <RiskGapCopyButton
            copyContent={note ?? ''}
            buttonDisabled={!note}
            tooltipEnabled={!!note}
            tooltipContent={'Copy notes to clipboard'}
            buttonClassName={twx(tw`absolute top-2 right-2`, {
              'opacity-50': !note,
            })}
          />
        </div>
      )}
      <div className={tw`flex w-full flex-wrap justify-between gap-3`}>
        <div className={tw`flex flex-row items-center gap-2`}>
          <Badge
            className={tw`rounded-full border bg-white text-content-main `}
            content={statusBadge.content}
            icon={undefined}
            size="sm"
            variant="meta"
          />
          {riskGap.substatuses.map((substatus) => (
            <Badge
              key={substatus}
              content={resolveSubstatusLabel(substatus)}
              className={tw`rounded border bg-white`}
              size="sm"
              textCasing="capitalize"
              variant="meta"
            />
          ))}
          <span className={tw`flex pt-.5 pl-1 text-content-subtle text-xs`}>
            Status Last Updated {formatFHIRDate(riskGap.lastStatusUpdateDate)} by{' '}
            {riskGap.lastUpdatedByDisplay}
          </span>
        </div>
        {riskGap.showPrimaryEditExperience && (
          <div className={tw`flex grow flex-row justify-end gap-3`}>
            {!isPendingGap(currentStatus) ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className={tw`border-none bg-transparent shadow-none hover:bg-transparent`}
                onClick={() => handleAction('pending')}
              >
                <FontAwesomeIcon icon={faClock} className={tw`h-3`} /> Save as Pending
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={tw`shadow-none`}
              onClick={() => handleAction('dismissed')}
            >
              <FontAwesomeIcon
                icon={faThumbsDown}
                className={tw`relative mr-1.5 h-3 scale-x-[-1.1] scale-y-[1.1]`}
              />
              Dismiss
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className={tw`border-none bg-primary-main text-content-inverse shadow-none hover:bg-primary-main`}
              onClick={() => handleAction('accepted-for-review')}
            >
              <FontAwesomeIcon icon={faThumbsUp} className={tw`relative mr-1 h-3`} />
              Accept
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
